import { Form } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { withRouter } from "react-router-dom";
import * as yup from "yup";

import { adminRequests } from "@/api/(requests)/admin";
import { adminRequestsTyped } from "@/api/(requests)/adminTyped";
import Button from "@/components/UI/Button";
import CardHeading from "@/components/UI/Headings/CardHeading";
import { CustomForm } from "@/component-library/CustomForm";
import { FormField, Dropdown, CheckboxOld, Text, TextTypes, Card } from "@/component-library";
import Hr from "@/components/UI/Hr";
import { SubmitControls } from "@/components/Common/SubmitControls";
import { timelineKeys } from "@/features/member-profile/(timeline)/api/keyFactory";

const actionDescription =
  "Change which practice and provider have access to a patient's records. Note making this " +
  "change requires obtaining and uploading a patient's written consent in advance.";

const ChangePatientPracticeCard = (props) => {
  const patientId = props.match.params.patientId;
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [pcpId, setPcpId] = useState(props.patientDetails && props.patientDetails.pcp_id);
  const [pcpName, setPcpName] = useState(props.patientDetails && props.patientDetails.pcp_name);
  const [pcpPracticeId, setPCPPracticeId] = useState(
    props.patientDetails && props.patientDetails.pcp_practice_id,
  );

  const [practices, setPractices] = useState([]);
  const [currentPractice, setCurrentPractice] = useState(null);
  const [providersOfPractice, setProvidersOfPractice] = useState([]);

  const submitChangePractice = async (formData) => {
    if (!formData.consent) {
      return;
    }

    const newPcp = formData.pcp;
    const newPcpPracticeId = formData.pcp_practice_id;
    const newPcpName =
      providersOfPractice.find((provider) => provider.value === newPcp)?.label ?? "";
    const changePracticeData = {
      new_pcp_id: newPcp,
      new_pcp_name: newPcpName,
      new_pcp_practice_id: newPcpPracticeId,
      patient_id: patientId,
    };

    await adminRequests.change_patient_practice({ data: changePracticeData });

    setCurrentPractice(
      practices.find((practice) => practice.value === newPcpPracticeId)?.label ?? "",
    );
    setPcpName(newPcpName);
    setPcpId(newPcp);
    setIsEditing(false);
    props.refetchPatientDetails();
    queryClient.invalidateQueries({ queryKey: timelineKeys.feed(patientId) });
  };

  const fetchAllPractices = useCallback(async (initialPcpPracticeId) => {
    // .js file — typed API call gets no compile-time type checking here
    const allPractices = await adminRequestsTyped.fetch_all_practices({
      context: { preventAllErrorToast: false, showLoader: false },
    });

    setCurrentPractice(
      allPractices.find((practice) => practice.id === initialPcpPracticeId)?.name ?? "",
    );

    const allPrimaryCarePractices = allPractices.filter(
      (practice) => practice.practice_type === "Primary Care",
    );
    const allPrimaryCarePracticesOptions = allPrimaryCarePractices.map((practice) => ({
      label: practice.name,
      value: practice.id,
    }));

    setPractices(allPrimaryCarePracticesOptions);
  }, []);

  const fetchAllProvidersInPractice = useCallback(async (pcpPracticeId) => {
    const data = { pcp_practice_id: pcpPracticeId };
    const allProvidersInPractice = await adminRequests.fetch_providers_by_pcp_practice_id({
      context: { showLoader: false },
      data,
    });
    const allProvidersInPracticeOptions = allProvidersInPractice.map((provider) => ({
      label: provider.name,
      value: provider.username,
    }));

    setProvidersOfPractice(allProvidersInPracticeOptions);
  }, []);

  const currentPracticeInfo = () => {
    return (
      <div>
        <div
          style={{
            display: "inline-flex",
            flexDirection: "column",
            marginTop: "1rem",
            maxWidth: "350px",
            width: "100%",
          }}
        >
          <span className="mb-1 font-weight-bold">Current Practice</span>
          <span>{currentPractice}</span>
        </div>
        <div
          style={{
            display: "inline-flex",
            flexDirection: "column",
            marginTop: "1rem",
            maxWidth: "350px",
            width: "100%",
          }}
        >
          <span className="mb-1 font-weight-bold">Current PCP</span>
          <span>{pcpName}</span>
        </div>
      </div>
    );
  };

  const form = (formikProps) => {
    const { errors, handleBlur, handleChange, handleFocus, setFieldValue, touched, values } =
      formikProps;

    return (
      <Form style={{ width: "100%" }}>
        <div className="mt-3" style={{ alignItems: "center", display: "flex" }}>
          <CheckboxOld
            checked={values.consent}
            disabled={!values.pcp_practice_id || !values.pcp}
            name="consent"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            style={{ marginRight: 10 }}
          />
          <Text className="fs-14" type={TextTypes.paragraph}>
            I obtained a written consent to share the patient’s information with the new practice.
          </Text>
        </div>
        {currentPracticeInfo()}
        <FormField
          className="mt-3"
          errorMessage={touched.pcp_practice_id ? errors.pcp_practice_id : ""}
          label="New Practice"
          style={{ maxWidth: 350 }}
        >
          <Dropdown
            error={touched.pcp_practice_id && errors.pcp_practice_id}
            isDisabled={!practices.length}
            name="pcp_practice_id"
            onBlur={handleBlur}
            onChange={(e) => {
              setProvidersOfPractice([]);
              setFieldValue("pcp", "");
              setPCPPracticeId(e.target.value);
              fetchAllProvidersInPractice(e.target.value);
              handleChange(e);
            }}
            onFocus={handleFocus}
            options={practices}
            placeholder={currentPractice}
            value={practices.find((practice) => practice.value === values.pcp_practice_id)}
          />
        </FormField>
        <FormField
          errorMessage={touched.pcp ? errors.pcp : ""}
          label="New PCP"
          style={{ maxWidth: 350 }}
        >
          <Dropdown
            error={touched.pcp && errors.pcp}
            isDisabled={!values.pcp_practice_id || !providersOfPractice.length}
            name="pcp"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            options={providersOfPractice}
            placeholder={pcpName}
            value={providersOfPractice.find((provider) => provider.value === values.pcp)}
          />
        </FormField>

        <div className="mb-3 d-lg-none">
          <Hr />
        </div>
        <SubmitControls cancelHandler={() => setIsEditing(false)} />
      </Form>
    );
  };

  const validation = yup.object().shape({
    pcp: yup.string().required("Provider is required"),
    pcp_practice_id: yup.string().required("Practice is required"),
  });

  const initialPcpPracticeIdRef = useRef(pcpPracticeId);

  useEffect(() => {
    fetchAllPractices(initialPcpPracticeIdRef.current);

    if (initialPcpPracticeIdRef.current) {
      fetchAllProvidersInPractice(initialPcpPracticeIdRef.current);
    }
  }, [fetchAllPractices, fetchAllProvidersInPractice]);

  return (
    <div className="AdminTab__ChangePatientPracticeCard">
      <Card className="p-3 p-lg-4 mb-3 mb-lg-4">
        <div className="d-flex justify-content-between">
          <CardHeading className="fs-16 m-0 mb-3" text="Change Patient Practice" />
          {!isEditing && (
            <Button
              className="ProviderProfile__ProfileDetailsCard-controls cursor mr-2 fs-14 mb-3 tw:font-bold"
              onClick={() => setIsEditing(!isEditing)}
            >
              Edit
            </Button>
          )}
        </div>
        <Hr />
        <div className="no-gutters row w-100">
          <div className="mt-4">
            <p className="admin-card-description mb-0">{actionDescription}</p>
            {!isEditing && currentPracticeInfo()}
            {isEditing && (
              <CustomForm
                initialValues={{
                  consent: false,
                  pcp: pcpId,
                  pcp_practice_id: pcpPracticeId,
                }}
                onSubmit={submitChangePractice}
                render={form}
                validationSchema={validation}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default withRouter(ChangePatientPracticeCard);
